import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomeContactForm from "@/components/HomeContactForm";
import useDocumentTitle from "@/lib/useDocumentTitle";
import { ArrowRight, ArrowUpRight, ShieldCheck, Clock, Scale, ChevronRight } from "lucide-react";

/**
 * SeoLanding — reusable long-form keyword-optimized landing page.
 * Drives search traffic to Register/Contact while packing white-hat SEO signals:
 * unique <title>, meta description, JSON-LD Article + FAQPage + BreadcrumbList,
 * H1 with primary keyword, dense semantic HTML, internal links, image alt text.
 *
 * @param {object} props
 * @param {string} props.slug              — URL slug (no leading slash)
 * @param {string} props.h1                — H1 heading (primary keyword phrase)
 * @param {string} props.subhead           — sub-headline / lead paragraph
 * @param {string} props.pageTitle         — <title> tag content
 * @param {string} props.pageDescription   — meta description
 * @param {string} props.crumb             — breadcrumb label
 * @param {string} props.eyebrow           — small overline text
 * @param {Array<{icon,label,value,desc}>} props.pillars — 3 authority pillars
 * @param {Array<{q,a}>} props.faq         — 4-6 FAQ items (rendered + JSON-LD)
 * @param {Array<{h,p}>} props.sections    — long-form body sections
 * @param {string} props.ctaTitle          — final CTA title
 */
export default function SeoLanding({
  slug, h1, subhead, pageTitle, pageDescription, crumb, eyebrow,
  pillars, faq, sections, ctaTitle,
}) {
  useDocumentTitle(pageTitle, pageDescription);
  const canonical = `https://azuregypsyguild.org/${slug}`;

  const ldArticle = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": h1,
    "description": pageDescription,
    "author": { "@type": "Organization", "name": "Azure Gypsy Guild", "url": "https://azuregypsyguild.org/" },
    "publisher": { "@type": "Organization", "name": "Azure Gypsy Guild", "logo": { "@type": "ImageObject", "url": "https://static.prod-images.emergentagent.com/jobs/4e2bdc98-ab8f-417d-b100-db42a3ecfaf3/images/2c4d4f557900b39c29a30c3708dd6bf80de599af925c4589b0748935140e0e0b.png" } },
    "mainEntityOfPage": canonical,
    "datePublished": "2026-02-01T00:00:00Z",
    "dateModified": "2026-07-30T00:00:00Z",
  };
  const ldBreadcrumb = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://azuregypsyguild.org/" },
      { "@type": "ListItem", "position": 2, "name": crumb, "item": canonical },
    ],
  };
  const ldFaq = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": faq.map((f) => ({
      "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-paper" data-testid={`seo-landing-${slug}`}>
      <Navbar />

      {/* Inject rich structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldArticle) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ldFaq) }} />
      <link rel="canonical" href={canonical} />

      <main>
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="max-w-[1400px] mx-auto px-6 md:px-12 pt-8">
          <ol className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-stone-500">
            <li><Link to="/" className="hover:text-stone-900">Home</Link></li>
            <li><ChevronRight className="h-3 w-3" /></li>
            <li className="text-stone-900">{crumb}</li>
          </ol>
        </nav>

        {/* ═══════ HERO ═══════ */}
        <section className="border-b border-stone-200">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-20 md:pb-28">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end">
              <div className="lg:col-span-8 fade-up">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-px bg-stone-900" />
                  <span className="overline">{eyebrow}</span>
                </div>
                <h1 className="font-serif font-light text-[9vw] md:text-[6vw] lg:text-[5.5vw] leading-[0.98] tracking-tighter text-stone-900 text-balance">
                  {h1}
                </h1>
                <p className="mt-8 text-lg md:text-xl text-stone-600 font-light leading-relaxed max-w-3xl text-pretty">
                  {subhead}
                </p>
                <div className="mt-10 flex flex-wrap gap-4 items-center">
                  <Link to="/register" className="btn-primary" data-testid="seo-cta-register">
                    Start my free case review <ArrowRight className="h-4 w-4" />
                  </Link>
                  <Link to="/contact" className="btn-ghost" data-testid="seo-cta-contact">
                    Or message a trustee now
                  </Link>
                </div>
              </div>

              <aside className="lg:col-span-4 border border-stone-200 bg-white p-6 md:p-8" aria-label="Case snapshot">
                <div className="overline mb-4">§ Case Snapshot</div>
                <ul className="space-y-5">
                  {pillars.map((p, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <p.icon className="h-5 w-5 mt-1 text-stone-900 flex-shrink-0" />
                      <div>
                        <div className="font-serif font-light text-2xl text-stone-900 leading-none">
                          {p.value}
                        </div>
                        <div className="overline mt-1 !tracking-[0.18em]">{p.label}</div>
                        <p className="text-xs text-stone-600 mt-1 font-light leading-relaxed">{p.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </aside>
            </div>
          </div>
        </section>

        {/* ═══════ LONG-FORM BODY SECTIONS ═══════ */}
        <article className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
            <div className="lg:col-span-3 hidden lg:block">
              <div className="sticky top-32 space-y-3">
                <div className="overline">§ On this page</div>
                {sections.map((s, i) => (
                  <a
                    key={i}
                    href={`#s${i}`}
                    className="block text-sm text-stone-600 hover:text-stone-900 font-light border-l border-stone-200 hover:border-stone-900 pl-4 py-1 transition-colors"
                  >
                    {s.h}
                  </a>
                ))}
                <a href="#faq" className="block text-sm text-stone-600 hover:text-stone-900 font-light border-l border-stone-200 hover:border-stone-900 pl-4 py-1 transition-colors">
                  FAQ
                </a>
              </div>
            </div>

            <div className="lg:col-span-9 space-y-16 md:space-y-24">
              {sections.map((s, i) => (
                <section key={i} id={`s${i}`} className="scroll-mt-32" data-testid={`seo-section-${i}`}>
                  <div className="caption text-stone-400 mb-4">§ {String(i + 1).padStart(2, "0")}</div>
                  <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-stone-900 text-balance mb-6">
                    {s.h}
                  </h2>
                  <div className="text-base md:text-lg text-stone-700 font-light leading-relaxed max-w-3xl space-y-5 text-pretty">
                    {Array.isArray(s.p) ? s.p.map((para, j) => <p key={j}>{para}</p>) : <p>{s.p}</p>}
                  </div>
                  {s.internal && (
                    <div className="mt-8 flex flex-wrap gap-3">
                      {s.internal.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-900 border-b border-stone-900 hover:text-stone-500 hover:border-stone-500 pb-0.5"
                          data-testid={`seo-internal-${link.to.replace(/[^a-z0-9]/gi, "-")}`}
                        >
                          {link.label} <ArrowUpRight className="h-3 w-3" />
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              ))}

              {/* FAQ */}
              <section id="faq" className="scroll-mt-32 border-t border-stone-200 pt-12" data-testid="seo-faq">
                <div className="caption text-stone-400 mb-4">§ FAQ</div>
                <h2 className="font-serif font-light text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight mb-10">
                  Frequently asked questions
                </h2>
                <dl className="divide-y divide-stone-200 border-t border-b border-stone-200">
                  {faq.map((f, i) => (
                    <div key={i} className="py-6 md:grid md:grid-cols-3 md:gap-8" data-testid={`seo-faq-${i}`}>
                      <dt className="font-serif text-lg md:text-xl text-stone-900 md:col-span-1">
                        {f.q}
                      </dt>
                      <dd className="text-stone-600 text-base font-light leading-relaxed md:col-span-2 mt-2 md:mt-0">
                        {f.a}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            </div>
          </div>
        </article>

        {/* ═══════ CTA ═══════ */}
        <section className="bg-stone-900 text-stone-100" data-testid="seo-closer">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="overline text-stone-400 mb-4">§ Next Step</div>
              <h2 className="font-serif font-light text-3xl md:text-5xl leading-[1.05] tracking-tight text-balance">
                {ctaTitle}
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/register"
                className="inline-flex items-center gap-3 border border-stone-100 text-stone-100 px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] hover:bg-stone-100 hover:text-stone-900 transition-colors"
                data-testid="seo-cta-final"
              >
                Start my free case review <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Inline contact form */}
        <section className="border-t border-stone-200 py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="overline mb-6">§ Or, message a trustee now</div>
              <h2 className="font-serif font-light text-3xl md:text-4xl leading-tight tracking-tight text-balance">
                Sheriff-sale &amp; court-deadline cases triaged first.
              </h2>
              <ul className="mt-8 space-y-3 text-sm text-stone-600 font-light">
                <li className="flex gap-3"><ShieldCheck className="h-4 w-4 mt-1 text-stone-900 flex-shrink-0" /> Same-day response on active sheriff-sale notices</li>
                <li className="flex gap-3"><Clock className="h-4 w-4 mt-1 text-stone-900 flex-shrink-0" /> One-business-day response on routine inquiries</li>
                <li className="flex gap-3"><Scale className="h-4 w-4 mt-1 text-stone-900 flex-shrink-0" /> Every reply comes from a senior Guild trustee</li>
              </ul>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-stone-200 bg-white p-6 md:p-10">
                <HomeContactForm />
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
